/**
 * useAnalyses Hook
 * TanStack Query hook for fetching user's analyses
 */

import { useQuery } from '@tanstack/react-query';
import { analysesService } from '@/services/api/analyses';
import type { Analysis, ProcessingRequest } from '@/types/api';

/**
 * Query key for analyses
 */
export const analysesQueryKey = ['analyses'] as const;

/**
 * Hook return type
 */
interface UseAnalysesResult {
  /** List of analyses */
  analyses: Analysis[];
  /** Whether analyses are being fetched */
  isLoading: boolean;
  /** Error if fetch failed */
  error: Error | null;
  /** Whether user has any analyses */
  hasAnalyses: boolean;
  /** Total count of analyses */
  total: number;
  /** Refetch analyses */
  refetch: () => void;
}

/**
 * Hook for fetching the current user's analyses
 */
export function useAnalyses(): UseAnalysesResult {
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: analysesQueryKey,
    queryFn: () => analysesService.listAnalyses(),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  const analyses = data?.analyses ?? [];
  const total = data?.total ?? 0;
  const hasAnalyses = analyses.length > 0;

  return {
    analyses,
    isLoading,
    error: error as Error | null,
    hasAnalyses,
    total,
    refetch,
  };
}

/**
 * Hook for fetching a single analysis
 */
export function useAnalysis(id: string | undefined) {
  return useQuery({
    queryKey: ['analysis', id],
    queryFn: () => analysesService.getAnalysis(id!),
    enabled: !!id,
    staleTime: 1000 * 60, // 1 minute
  });
}

/**
 * Query key for processing requests
 */
export const processingRequestsQueryKey = ['processingRequests'] as const;

/**
 * Hook return type for processing requests
 */
interface UseProcessingRequestsResult {
  /** List of processing requests */
  requests: ProcessingRequest[];
  /** Whether requests are being fetched */
  isLoading: boolean;
  /** Error if fetch failed */
  error: Error | null;
  /** Total count of requests */
  total: number;
  /** Refetch requests */
  refetch: () => void;
}

/**
 * Hook for fetching the current user's processing requests
 */
export function useProcessingRequests(): UseProcessingRequestsResult {
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: processingRequestsQueryKey,
    queryFn: () => analysesService.listProcessingRequests(1, 3),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  // API returns array directly
  const requests = data ?? [];
  const total = requests.length;

  return {
    requests,
    isLoading,
    error: error as Error | null,
    total,
    refetch,
  };
}

/**
 * Filter options for processing requests
 */
export interface ProcessingRequestsFilterOptions {
  page: number;
  pageSize: number;
  status: number | null;
  sortOrder: 'asc' | 'desc';
}

/**
 * Hook return type for filtered processing requests
 */
interface UseFilteredProcessingRequestsResult {
  /** List of processing requests */
  requests: ProcessingRequest[];
  /** Whether requests are being fetched */
  isLoading: boolean;
  /** Error if fetch failed */
  error: Error | null;
  /** Total count of requests (for pagination) */
  total: number;
  /** Refetch requests */
  refetch: () => void;
}

/**
 * Hook for fetching filtered and paginated processing requests
 */
export function useFilteredProcessingRequests(
  options: ProcessingRequestsFilterOptions
): UseFilteredProcessingRequestsResult {
  const { page, pageSize, status, sortOrder } = options;

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['processingRequests', 'filtered', page, pageSize, status, sortOrder],
    queryFn: () =>
      analysesService.listProcessingRequestsFiltered({
        page,
        pageSize,
        status,
        sortBy: 'createdAt',
        sortOrder,
      }),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  const requests = data?.requests ?? [];
  const total = data?.total ?? 0;

  return {
    requests,
    isLoading,
    error: error as Error | null,
    total,
    refetch,
  };
}
